/**
 * Performance Monitoring Utilities
 * Track and report Core Web Vitals
 */

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

/**
 * Report Web Vitals to analytics
 */
export const reportWebVitals = (metric: PerformanceMetric): void => {
  // Send to Google Analytics
  if (window.gtag) {
    window.gtag('event', metric.name, {
      value: Math.round(metric.value),
      metric_rating: metric.rating,
      non_interaction: true
    });
  }

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Performance] ${metric.name}:`, {
      value: `${Math.round(metric.value)}ms`,
      rating: metric.rating
    });
  }
};

/**
 * Measure First Contentful Paint (FCP)
 */
export const measureFCP = (): void => {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          const value = entry.startTime;
          const rating = value <= 1800 ? 'good' : value <= 3000 ? 'needs-improvement' : 'poor';
          reportWebVitals({ name: 'FCP', value, rating });
          observer.disconnect();
        }
      }
    });
    observer.observe({ entryTypes: ['paint'] });
  }
};

/**
 * Measure Largest Contentful Paint (LCP)
 */
export const measureLCP = (): void => {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      const value = lastEntry.startTime;
      const rating = value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor';
      reportWebVitals({ name: 'LCP', value, rating });
    });
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
  }
};

/**
 * Measure First Input Delay (FID)
 */
export const measureFID = (): void => {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const fidEntry = entry as PerformanceEventTiming;
        const value = fidEntry.processingStart - fidEntry.startTime;
        const rating = value <= 100 ? 'good' : value <= 300 ? 'needs-improvement' : 'poor';
        reportWebVitals({ name: 'FID', value, rating });
        observer.disconnect();
      }
    });
    observer.observe({ entryTypes: ['first-input'] });
  }
};

/**
 * Measure Cumulative Layout Shift (CLS)
 */
export const measureCLS = (): void => {
  if ('PerformanceObserver' in window) {
    let clsValue = 0;
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const layoutShift = entry as LayoutShift;
        if (!layoutShift.hadRecentInput) {
          clsValue += layoutShift.value;
        }
      }
    });
    observer.observe({ entryTypes: ['layout-shift'] });

    // Report CLS after 5 seconds
    setTimeout(() => {
      const rating = clsValue <= 0.1 ? 'good' : clsValue <= 0.25 ? 'needs-improvement' : 'poor';
      reportWebVitals({ name: 'CLS', value: clsValue, rating });
      observer.disconnect();
    }, 5000);
  }
};

/**
 * Measure Time to Interactive (TTI)
 */
export const measureTTI = (): void => {
  if ('PerformanceObserver' in window) {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          const value = navigation.domInteractive;
          const rating = value <= 3800 ? 'good' : value <= 7300 ? 'needs-improvement' : 'poor';
          reportWebVitals({ name: 'TTI', value, rating });
        }
      }, 0);
    });
  }
};

/**
 * Initialize all performance monitoring
 */
export const initPerformanceMonitoring = (): void => {
  measureFCP();
  measureLCP();
  measureFID();
  measureCLS();
  measureTTI();
};

// Type definitions for Performance APIs
interface LayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}
